/**
 * Helpers for the mobile bottom tab bar: which pages show it, and which tab is active.
 */

const BROWSE_PAGES = ['LandingPage', 'SearchPage', 'SearchPageWithListingType'];
const LISTINGS_PAGES = ['ManageListingsPage'];
const TRANSACTIONS_PAGE_PREFIX = 'TransactionsPage';

/**
 * Tab-root pages where the floating bottom nav should appear.
 * Matches MOBILE-NAV handoff: hide on detail, checkout, wizards, account pages, etc.
 *
 * @param {string|null|undefined} currentPage resolved route name (may include tab suffix)
 * @returns {boolean}
 */
export const shouldShowMobileBottomNav = currentPage => {
  if (!currentPage || typeof currentPage !== 'string') {
    return false;
  }
  if (BROWSE_PAGES.includes(currentPage)) {
    return true;
  }
  if (LISTINGS_PAGES.includes(currentPage)) {
    return true;
  }
  if (currentPage.indexOf(TRANSACTIONS_PAGE_PREFIX) === 0) {
    return true;
  }
  return false;
};

/**
 * @param {string|null|undefined} currentPage
 * @returns {'browse'|'listings'|'transactions'|null}
 */
export const getActiveMobileBottomTab = currentPage => {
  if (!currentPage || typeof currentPage !== 'string') {
    return null;
  }
  if (BROWSE_PAGES.includes(currentPage)) {
    return 'browse';
  }
  if (LISTINGS_PAGES.includes(currentPage)) {
    return 'listings';
  }
  if (currentPage.indexOf(TRANSACTIONS_PAGE_PREFIX) === 0) {
    return 'transactions';
  }
  return null;
};

export const MOBILE_BOTTOM_NAV_BODY_CLASS = 'hasMobileBottomNav';

/** Body class while a mobile overlay (e.g. search filters sheet) covers the tab bar. */
export const MOBILE_BOTTOM_NAV_SUPPRESSED_CLASS = 'mobileBottomNavSuppressed';

/**
 * Match SearchFiltersMobile / ModalInMobile bottom-sheet timing (0.28s ease-out).
 * Used by MobileBottomNav CSS transition so fade in/out stays in sync with the sheet.
 */
export const MOBILE_BOTTOM_NAV_SUPPRESS_MS = 280;

/** Default reason used by overlay sheets (e.g. search filters). */
export const MOBILE_BOTTOM_NAV_SUPPRESS_REASON_DEFAULT = 'default';

/** Reason used while the software keyboard is open. */
export const MOBILE_BOTTOM_NAV_SUPPRESS_REASON_KEYBOARD = 'keyboard';

/**
 * Keyboard typically takes 250px+; Safari toolbar show/hide is ~40–90px.
 * Used to tell those apart via visualViewport.
 */
export const KEYBOARD_OBSCURED_THRESHOLD_PX = 120;

/**
 * Keep the bar hidden after blur while iOS finishes the keyboard animation.
 * Also covers focusout→focusin when moving between fields.
 */
export const KEYBOARD_FOCUS_OUT_GRACE_MS = 350;

const INPUT_TYPES_WITHOUT_KEYBOARD = [
  'button',
  'checkbox',
  'radio',
  'submit',
  'reset',
  'file',
  'hidden',
  'range',
  'color',
  'image',
];

const suppressReasons = new Set();

const applySuppressClass = () => {
  if (typeof document === 'undefined') {
    return;
  }
  const { body } = document;
  if (!body) {
    return;
  }
  if (suppressReasons.size > 0) {
    body.classList.add(MOBILE_BOTTOM_NAV_SUPPRESSED_CLASS);
  } else {
    body.classList.remove(MOBILE_BOTTOM_NAV_SUPPRESSED_CLASS);
  }
};

/**
 * Fade the floating bottom nav out/in via a body class (CSS transition on the nav).
 * Safe no-op when `document` is unavailable (SSR).
 *
 * Reasons are independent so a filter sheet and the software keyboard can both
 * hide the bar without one un-suppressing the other.
 *
 * @param {boolean} suppressed
 * @param {string} [reason]
 */
export const setMobileBottomNavSuppressed = (
  suppressed,
  reason = MOBILE_BOTTOM_NAV_SUPPRESS_REASON_DEFAULT
) => {
  if (typeof document === 'undefined') {
    return;
  }
  if (suppressed) {
    suppressReasons.add(reason);
  } else {
    suppressReasons.delete(reason);
  }
  applySuppressClass();
};

/**
 * @param {Element|null|undefined} el
 * @returns {boolean}
 */
export const isKeyboardTextField = el => {
  if (!el || el.nodeType !== 1) {
    return false;
  }
  const tag = el.tagName;
  if (tag === 'TEXTAREA') {
    return true;
  }
  if (el.isContentEditable) {
    return true;
  }
  if (tag !== 'INPUT') {
    return false;
  }
  const type = (el.getAttribute('type') || 'text').toLowerCase();
  return INPUT_TYPES_WITHOUT_KEYBOARD.indexOf(type) === -1;
};

const visualViewportHeight = win => {
  const vv = win && win.visualViewport;
  if (vv && typeof vv.height === 'number') {
    return vv.height;
  }
  return typeof win.innerHeight === 'number' ? win.innerHeight : 0;
};

/**
 * iOS Safari often shrinks visualViewport while leaving window.innerHeight alone.
 * Android Chrome usually resizes both together, so this is only a backup signal.
 *
 * @param {Window} [win]
 * @returns {boolean}
 */
export const isSoftwareKeyboardOpen = (
  win = typeof window === 'undefined' ? undefined : window
) => {
  if (!win) {
    return false;
  }
  const vv = win.visualViewport;
  if (!vv) {
    return false;
  }
  const offsetTop = vv.offsetTop || 0;
  const obscured = win.innerHeight - vv.height - offsetTop;
  return obscured > KEYBOARD_OBSCURED_THRESHOLD_PX;
};

/**
 * Decide whether the tab bar should hide for the software keyboard.
 *
 * Viewport shrink without a text-field session is ignored (pinch-zoom).
 * After a shrink has been seen, restoring the viewport shows the bar even if
 * the field is still focused (Android back dismisses the keyboard without blur).
 *
 * @param {Object} opts
 * @param {boolean} opts.mobile
 * @param {boolean} opts.focused
 * @param {boolean} [opts.recentlyFocused]
 * @param {boolean} [opts.viewportObscured]
 * @param {boolean} [opts.keyboardWasShown]
 * @returns {boolean}
 */
export const shouldSuppressNavForKeyboard = ({
  mobile,
  focused,
  recentlyFocused = false,
  viewportObscured = false,
  keyboardWasShown = false,
} = {}) => {
  if (!mobile) {
    return false;
  }
  const inTextSession = focused || recentlyFocused;
  if (!inTextSession) {
    return false;
  }
  if (viewportObscured) {
    return true;
  }
  if (keyboardWasShown) {
    return false;
  }
  return !!focused;
};

/**
 * Listen for keyboard / focus changes and hide the bottom nav while typing.
 * Returns an unsubscribe that also clears the keyboard suppress reason.
 *
 * @returns {() => void}
 */
export const subscribeMobileBottomNavKeyboard = () => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let baselineHeight = null;
  let keyboardWasShown = false;
  let lastFocusOutAt = 0;
  let focusOutTimer = null;

  const isMobile = () => !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);

  const sync = () => {
    const mobile = isMobile();
    const focused = isKeyboardTextField(document.activeElement);
    const recentlyFocused = Date.now() - lastFocusOutAt < KEYBOARD_FOCUS_OUT_GRACE_MS;

    if (!mobile || (!focused && !recentlyFocused)) {
      baselineHeight = null;
      keyboardWasShown = false;
      setMobileBottomNavSuppressed(false, MOBILE_BOTTOM_NAV_SUPPRESS_REASON_KEYBOARD);
      return;
    }

    const height = visualViewportHeight(window);
    if (baselineHeight == null || height > baselineHeight) {
      baselineHeight = height;
    }
    const obscuredFromBaseline = baselineHeight - height > KEYBOARD_OBSCURED_THRESHOLD_PX;
    const viewportObscured = obscuredFromBaseline || isSoftwareKeyboardOpen(window);
    if (viewportObscured) {
      keyboardWasShown = true;
    }

    setMobileBottomNavSuppressed(
      shouldSuppressNavForKeyboard({
        mobile,
        focused,
        recentlyFocused,
        viewportObscured,
        keyboardWasShown,
      }),
      MOBILE_BOTTOM_NAV_SUPPRESS_REASON_KEYBOARD
    );
  };

  const onFocusIn = () => {
    if (focusOutTimer) {
      clearTimeout(focusOutTimer);
      focusOutTimer = null;
    }
    lastFocusOutAt = 0;
    sync();
  };

  const onFocusOut = () => {
    lastFocusOutAt = Date.now();
    if (focusOutTimer) {
      clearTimeout(focusOutTimer);
    }
    focusOutTimer = setTimeout(() => {
      focusOutTimer = null;
      sync();
    }, KEYBOARD_FOCUS_OUT_GRACE_MS);
  };

  const vv = window.visualViewport;

  window.addEventListener('resize', sync);
  window.addEventListener('focusin', onFocusIn);
  window.addEventListener('focusout', onFocusOut);
  if (vv) {
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
  }
  sync();

  return () => {
    if (focusOutTimer) {
      clearTimeout(focusOutTimer);
      focusOutTimer = null;
    }
    window.removeEventListener('resize', sync);
    window.removeEventListener('focusin', onFocusIn);
    window.removeEventListener('focusout', onFocusOut);
    if (vv) {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    }
    setMobileBottomNavSuppressed(false, MOBILE_BOTTOM_NAV_SUPPRESS_REASON_KEYBOARD);
  };
};
